class CartDrawerRecommendations extends HTMLElement {
  static fetchCache = new Map();

  constructor() {
    super();
    this.container = null;
    this.openHandler = null;
    this.initialized = false;
  }

  connectedCallback() {
    this.container = this.querySelector('[data-recommendations-container]');
    if (!this.container) return;

    // When inserted via cart-drawer renderContents, .open() runs AFTER the
    // innerHTML swap, so drawer-open isn't on <body> yet at this moment.
    // Defer one microtask so we observe the post-renderContents state.
    queueMicrotask(() => {
      if (this.initialized) return;
      if (document.body.classList.contains('drawer-open')) {
        this.init();
        return;
      }
      this.openHandler = () => this.init();
      document.addEventListener('cartOpen', this.openHandler, { once: true });
    });
  }

  disconnectedCallback() {
    if (this.openHandler) {
      document.removeEventListener('cartOpen', this.openHandler);
      this.openHandler = null;
    }
  }

  async init() {
    if (this.initialized) return;
    this.initialized = true;

    const items = this.parseItems();
    const limit = parseInt(this.dataset.limit, 10) || 8;
    const theme = (this.dataset.theme || '').trim();
    const shop = this.dataset.shopDomain;

    // Groups become tabs. Curated accessories for a machine in the cart are
    // split the same way as the product page's "Complete your setup" builder:
    // the machine's own brand first, everything else under thread & essentials.
    // Boost's "You might also like" is always the last tab and loads lazily.
    const groups = [];
    const curated = this.filterProducts(this.parseCurated(), items);
    if (curated.length) {
      const machine = items.slice().reverse().find((i) => this.isMachine(i));
      const vendor = ((machine && machine.vendor) || '').trim();
      const vendorKey = vendor.toLowerCase();
      const own = vendorKey
        ? curated.filter((p) => (p.vendor || '').trim().toLowerCase() === vendorKey)
        : curated;
      const rest = curated.filter((p) => !own.includes(p));
      if (own.length) {
        groups.push({
          key: 'own',
          label: vendor ? `${vendor} accessories` : 'Accessories',
          cards: this.expandToCards(own, limit),
          products: own,
        });
      }
      if (rest.length) {
        groups.push({
          key: 'essentials',
          label: 'Thread, needles & essentials',
          cards: this.expandToCards(rest, limit),
          products: rest,
        });
      }
      this.setHeading('Complete your setup');
    }

    if (shop) {
      groups.push({
        key: 'boost',
        label: 'You might also like',
        lazy: () => this.loadBoostCards(shop, items, limit, theme),
      });
    }

    if (!groups.length) return this.hide();
    this.renderGroups(groups);
  }

  // Boost frequently-bought-together (plus fabric theme mates), as before.
  async loadBoostCards(shop, items, limit, theme) {
    const themeCardLimit = theme ? Math.floor(limit / 2) : 0;
    const boostCardLimit = limit - themeCardLimit;

    const [boostProducts, themeProducts] = await Promise.all([
      this.fetchBoostRecommendations(shop, items),
      theme ? this.fetchThemeMates(shop, theme, items) : Promise.resolve([]),
    ]);

    const seen = new Set(items.map((i) => Number(i.id)));
    const dedupe = (list) =>
      list.filter((p) => {
        const id = Number(p.id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    const boostDedup = dedupe(boostProducts);
    const themeDedup = dedupe(themeProducts);

    const cards = [
      ...this.expandToCards(boostDedup, boostCardLimit),
      ...this.expandToCards(themeDedup, themeCardLimit),
    ];
    return { cards, products: [...boostDedup, ...themeDedup] };
  }

  parseCurated() {
    try {
      const raw = this.dataset.curatedProducts;
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  setHeading(text) {
    const h = this.querySelector('[data-rec-heading]');
    if (h) h.textContent = text;
  }

  parseItems() {
    try {
      return JSON.parse(this.dataset.cartItems || '[]');
    } catch (e) {
      return [];
    }
  }

  isMachine(item) {
    return (item.type || '').toLowerCase() === 'sewing machines';
  }

  pickSourceProductId(items) {
    if (!items.length) return null;
    const machine = items.find((i) => this.isMachine(i));
    if (machine) return machine.id;
    const highest = items.reduce((max, i) => (i.price > (max?.price ?? -1) ? i : max), null);
    return highest ? highest.id : null;
  }

  async fetchBoostRecommendations(shop, cartItems) {
    const sourceProductId = this.pickSourceProductId(cartItems);
    const cacheKey = String(sourceProductId || 'empty');
    if (CartDrawerRecommendations.fetchCache.has(cacheKey)) {
      return CartDrawerRecommendations.fetchCache.get(cacheKey);
    }

    const products = await this._fetchBoostDiscovery(shop, sourceProductId);
    const filtered = this.filterProducts(products, cartItems);

    if (sourceProductId && filtered.length < 3) {
      console.log('[CartDrawerRecs] fallback → bestsellers');
      const fallback = await this._fetchBoostDiscovery(shop, null);
      CartDrawerRecommendations.fetchCache.set('empty', fallback);
      return this.filterProducts(fallback, cartItems);
    }

    CartDrawerRecommendations.fetchCache.set(cacheKey, products);
    return filtered;
  }

  async _fetchBoostDiscovery(shop, sourceProductId) {
    const API = 'https://services.mybcapps.com/discovery/recommend';
    const wid = this.dataset.widgetId || 'cartpage-1';
    const useFBT = !!sourceProductId;
    const overfetch = 30;

    const body = useFBT
      ? {
          shop,
          recommendationType: 'frequently-bought-together',
          modelType: 'FBT',
          secondaryAlgorithm: 'bestsellers',
          productIds: [Number(sourceProductId)],
          limit: overfetch,
          layoutDisplay: 'carousel',
          wid,
          locale: 'en',
          return_all_currency_fields: false,
          pg: 'cart_page',
        }
      : {
          shop,
          recommendationType: 'bestsellers',
          productIds: [],
          limit: overfetch,
          layoutDisplay: 'carousel',
          wid,
          locale: 'en',
          return_all_currency_fields: false,
          pg: 'cart_page',
        };

    const params = new URLSearchParams({
      shop,
      recommendationType: body.recommendationType,
      limit: String(overfetch),
      wid,
      event_type: 'init',
      pg: body.pg,
    });

    console.log('[CartDrawerRecs] discovery request', { url: `${API}?${params.toString()}`, body });

    const res = await fetch(`${API}?${params.toString()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    const products = Array.isArray(data.products) ? data.products : [];
    console.log('[CartDrawerRecs] discovery response', { status: res.status, useFBT, total: products.length });
    return products;
  }

  async fetchThemeMates(shop, theme, cartItems) {
    const API = 'https://services.mybcapps.com/bc-sf-filter/search';
    const params = new URLSearchParams();
    params.set('shop', shop);
    params.set('q', theme);
    params.append('pf_m_custom_themes-list[]', theme);
    params.append('pf_t[]', 'Fabric');
    params.set('product_available', 'true');
    params.set('limit', '20');

    const url = `${API}?${params.toString()}`;
    console.log('[CartDrawerRecs theme] request', url);

    try {
      const res = await fetch(url);
      const data = await res.json();
      const products = Array.isArray(data.products) ? data.products : [];
      const filtered = this.filterProducts(products, cartItems);
      console.log('[CartDrawerRecs theme] response', { status: res.status, theme, total: products.length, afterFilter: filtered.length });
      return filtered;
    } catch (err) {
      console.error('[CartDrawerRecs theme] fetch failed', err);
      return [];
    }
  }

  filterProducts(products, cartItems) {
    const cartIds = new Set(cartItems.map((i) => Number(i.id)));
    return products.filter((p) => {
      if ((p.product_type || '').toLowerCase() === 'sewing machines') return false;
      if (p.available === false) return false;
      if (cartIds.has(Number(p.id))) return false;
      return true;
    });
  }

  hide() {
    this.removeAttribute('data-ready');
  }

  expandToCards(products, limit) {
    const cards = [];
    for (const p of products) {
      if (cards.length >= limit) break;
      if (this.isFabric(p)) {
        const available = (Array.isArray(p.variants) ? p.variants : []).filter(
          (v) => v.available !== false
        );
        for (const v of available) {
          if (cards.length >= limit) break;
          cards.push({ product: p, variant: v });
        }
      } else {
        cards.push({ product: p, variant: null });
      }
    }
    return cards;
  }

  render(products, cartItems, limit) {
    const filtered = this.filterProducts(products, cartItems);
    const cards = this.expandToCards(filtered, limit);
    this.renderGroups([{ key: 'all', label: 'You might also like', cards, products: filtered }]);
  }

  // Tabs across the top, one panel per group. A single group renders with no
  // tab bar. Each panel shows a few rows and a "Show N more" button so the
  // drawer's checkout button stays in reach.
  renderGroups(groups) {
    this.groups = groups;
    this.productsById = this.productsById || new Map();

    const tabs = groups.length > 1
      ? `<div class="cart-drawer-recs__tabs" role="tablist">${groups
          .map(
            (g, i) => `<button type="button" role="tab" class="cart-drawer-recs__tab" id="rec-tab-${g.key}" aria-controls="rec-panel-${g.key}" aria-selected="${i === 0}" data-rec-tab="${g.key}">${this.escapeHtml(g.label)}</button>`
          )
          .join('')}</div>`
      : '';

    const panels = groups
      .map(
        (g, i) => `<div class="cart-drawer-recs__panel" role="tabpanel" id="rec-panel-${g.key}" aria-labelledby="rec-tab-${g.key}" data-rec-panel="${g.key}"${i === 0 ? '' : ' hidden'}><div class="cart-drawer-recs__list" data-rec-list></div></div>`
      )
      .join('');

    this.container.innerHTML = tabs + panels;

    this.container.querySelectorAll('[data-rec-tab]').forEach((tab) => {
      tab.addEventListener('click', () => this.activateGroup(tab.dataset.recTab));
    });

    groups.forEach((g) => {
      if (g.cards) this.fillPanel(g);
    });

    const first = groups[0];
    if (first.lazy) {
      this.activateGroup(first.key);
    } else {
      this.setAttribute('data-ready', '');
    }
  }

  async activateGroup(key) {
    const group = this.groups.find((g) => g.key === key);
    if (!group) return;

    this.container.querySelectorAll('[data-rec-tab]').forEach((tab) => {
      tab.setAttribute('aria-selected', tab.dataset.recTab === key ? 'true' : 'false');
    });
    this.container.querySelectorAll('[data-rec-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.recPanel !== key;
    });

    if (group.lazy && !group.cards && !group.loading) {
      group.loading = true;
      const panel = this.container.querySelector(`[data-rec-panel="${key}"]`);
      const list = panel && panel.querySelector('[data-rec-list]');
      if (list) list.innerHTML = '<p class="cart-drawer-recs__loading">Loading…</p>';
      try {
        const { cards, products } = await group.lazy();
        group.cards = cards;
        group.products = products;
      } catch (err) {
        console.error('[CartDrawerRecs] group load failed', key, err);
        group.cards = [];
        group.products = [];
      }
      group.loading = false;
      if (!group.cards.length) return this.dropGroup(key);
      this.fillPanel(group);
      this.setAttribute('data-ready', '');
    }
  }

  fillPanel(group) {
    const panel = this.container.querySelector(`[data-rec-panel="${group.key}"]`);
    if (!panel) return;
    const list = panel.querySelector('[data-rec-list]');
    (group.products || []).forEach((p) => this.productsById.set(String(p.id), p));

    list.innerHTML = group.cards.map((c) => this.renderCard(c)).join('');
    list.querySelectorAll('[data-rec-add]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleAdd(e, btn));
    });
    list.querySelectorAll('[data-rec-configure]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.openConfig(e, btn));
    });
    this.collapseRows(panel);
  }

  // Remove a tab whose lazy load returned nothing; hide the block if none remain.
  dropGroup(key) {
    this.groups = this.groups.filter((g) => g.key !== key);
    const tab = this.container.querySelector(`[data-rec-tab="${key}"]`);
    const panel = this.container.querySelector(`[data-rec-panel="${key}"]`);
    if (tab) tab.remove();
    if (panel) panel.remove();
    if (!this.groups.length) return this.hide();
    if (this.groups.length === 1) {
      const bar = this.container.querySelector('.cart-drawer-recs__tabs');
      if (bar) bar.remove();
    }
    if (!this.container.querySelector('[data-rec-panel]:not([hidden])')) {
      this.activateGroup(this.groups[0].key);
    }
  }

  // Show the first few rows and a "Show N more" button, like the product page
  // builder, so a long list doesn't push the checkout button off screen.
  collapseRows(panel) {
    const visible = parseInt(this.dataset.visibleRows, 10) || 3;
    const rows = Array.from(panel.querySelectorAll('.cart-drawer-recs__card'));
    const existing = panel.querySelector('[data-rec-more]');
    if (existing) existing.remove();
    if (rows.length <= visible) return;

    rows.slice(visible).forEach((row) => row.classList.add('cart-drawer-recs__card--hidden'));
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'cart-drawer-recs__more';
    more.setAttribute('data-rec-more', '');
    more.textContent = `Show ${rows.length - visible} more`;
    more.addEventListener('click', () => {
      rows.forEach((row) => row.classList.remove('cart-drawer-recs__card--hidden'));
      more.remove();
    });
    panel.appendChild(more);
  }

  isFabric(p) {
    return (p.product_type || '').toLowerCase() === 'fabric';
  }

  getRangeName(p) {
    if (p.range_name) return p.range_name;
    const mf = p.metafields;
    if (Array.isArray(mf)) {
      const found = mf.find(
        (m) => m && m.namespace === 'customAttributes' && m.key === 'range-name'
      );
      if (found && found.value) return found.value;
    } else if (mf && mf.customAttributes && mf.customAttributes['range-name']) {
      return mf.customAttributes['range-name'];
    }
    if (p.customAttributes && p.customAttributes['range-name']) {
      return p.customAttributes['range-name'];
    }
    return '';
  }

  renderCard({ product: p, variant }) {
    const imgSrc = this.sizedImage(
      variant ? this.getVariantImage(variant, p) : this.getImage(p),
      200,
      200
    );
    const price = this.formatPrice(p);
    const handle = encodeURIComponent(p.handle || '');
    const href = variant ? `/products/${handle}?variant=${variant.id}` : `/products/${handle}`;

    let title, action, eyebrow = '';
    if (variant) {
      // Fabric variant card: title is the design name, configure opens cut-type step directly
      title = this.escapeHtml(this.getVariantLabel(variant));
      const vendor = (p.vendor || '').trim();
      const rangeName = (this.getRangeName(p) || '').trim();
      const parts = [vendor, rangeName].filter(Boolean);
      if (parts.length) {
        eyebrow = `<span class="cart-drawer-recs__eyebrow">${this.escapeHtml(parts.join(' · '))}</span>`;
      }
      action = `<button type="button" class="cart-drawer-recs__view" data-rec-configure data-product-id="${p.id}" data-variant-id="${variant.id}">Select</button>`;
    } else {
      title = this.escapeHtml(p.title || '');
      const variants = Array.isArray(p.variants) ? p.variants : [];
      const available = variants.filter((v) => v.available !== false);
      const isSimple = available.length === 1;
      if (isSimple) {
        action = `<button type="button" class="cart-drawer-recs__add" data-rec-add data-variant-id="${available[0].id}" aria-label="Add ${title} to cart">Add</button>`;
      } else {
        action = `<button type="button" class="cart-drawer-recs__view" data-rec-configure data-product-id="${p.id}">Select</button>`;
      }
    }

    // Row anatomy mirrors .setup-card in the product page's "Complete your
    // setup" builder: thumbnail, title, teal price, pill button on the right.
    return `
      <div class="cart-drawer-recs__card">
        <a href="${href}" class="cart-drawer-recs__image-link" tabindex="-1">
          <img src="${this.escapeHtml(imgSrc)}" alt="" loading="lazy" width="112" height="112">
        </a>
        <div class="cart-drawer-recs__info">
          ${eyebrow}
          <a href="${href}" class="cart-drawer-recs__title">${title}</a>
          <span class="cart-drawer-recs__meta"><span class="cart-drawer-recs__price">${price}</span></span>
        </div>
        ${action}
      </div>
    `;
  }

  getImage(p) {
    if (Array.isArray(p.images_info) && p.images_info.length) return p.images_info[0].src;
    if (p.image && p.image.src) return p.image.src;
    if (typeof p.image === 'string') return p.image;
    return '';
  }

  sizedImage(url, width, height) {
    if (!url) return '';
    return url.replace(/(_\d+x\d+)?(\.(jpg|jpeg|png|gif|webp))/i, `_${width}x${height}$2`);
  }

  formatPrice(p) {
    const variants = Array.isArray(p.variants) ? p.variants : [];
    const raw = variants[0]?.price ?? p.price_min;
    const pounds = Number(raw);
    if (isNaN(pounds)) return '';
    const fractionDigits = Number.isInteger(pounds) ? 0 : 2;
    return '£' + pounds.toLocaleString('en-GB', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }

  escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[c]));
  }

  async handleAdd(e, btn) {
    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding…';

    try {
      await this.postCartAdd({ variantId: btn.dataset.variantId, quantity: 1 });
    } catch (err) {
      console.error('[CartDrawerRecs add]', err);
      btn.disabled = false;
      btn.textContent = originalText;
    }
  }

  async postCartAdd({ variantId, quantity, properties }) {
    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer
      ? [
          ...cartDrawer.getSectionsToRender().map((s) => s.id),
          ...cartDrawer.getIconsToRender().map((s) => s.id),
        ]
      : ['cart-drawer', 'cart-icon-bubble'];

    // Underscore-prefixed property names are hidden from cart/checkout UI but
    // still stored on the line item and order. Tags every add from the cart
    // drawer upsell so these can be attributed later.
    const item = {
      id: variantId,
      quantity,
      properties: { ...(properties || {}), _has_part: 'included' },
    };

    const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections, sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || 'Add to cart failed');

    if (data.sections && cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(data);
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    }
    document.dispatchEvent(new Event('cartChanged'));
    return data;
  }

  openConfig(e, btn) {
    e.preventDefault();
    const product = this.productsById?.get(btn.dataset.productId);
    if (!product) return;

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const available = variants.filter((v) => v.available !== false);
    if (!available.length) return;

    // Fabric cards carry a pre-determined variant ID; non-fabric multi-variant needs picker
    const preselectedId = btn.dataset.variantId;
    const preselected = preselectedId
      ? variants.find((v) => String(v.id) === String(preselectedId)) || null
      : null;
    const multiVariant = !preselected && available.length > 1;
    const initialVariant = preselected || (multiVariant ? null : available[0]);

    let panel = this.querySelector('[data-config-panel]');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'cart-drawer-recs__config';
      panel.setAttribute('data-config-panel', '');
      this.appendChild(panel);
    }
    panel.innerHTML = this.renderConfig(product, available, initialVariant);
    panel.dataset.productId = product.id;
    if (initialVariant) panel.dataset.variantId = initialVariant.id;
    this.setAttribute('data-configuring', '');

    panel.querySelector('[data-config-back]').addEventListener('click', () => this.closeConfig());
    panel.querySelectorAll('[data-variant-option]').forEach((b) =>
      b.addEventListener('click', () => this.setVariant(panel, b))
    );
    panel.querySelectorAll('[data-cut]').forEach((b) =>
      b.addEventListener('click', () => this.setCutType(panel, b.dataset.cut))
    );
    panel.querySelector('[data-qty-minus]').addEventListener('click', () => this.bumpQty(panel, -1));
    panel.querySelector('[data-qty-plus]').addEventListener('click', () => this.bumpQty(panel, 1));
    panel.querySelector('[data-qty-input]').addEventListener('input', () => this.updateQtyLabel(panel));
    panel.querySelector('[data-config-add]').addEventListener('click', () => this.handleConfigAdd(panel));

    this.updateQtyLabel(panel);
  }

  closeConfig() {
    this.removeAttribute('data-configuring');
  }

  setCutType(panel, cutType) {
    panel.querySelectorAll('[data-cut]').forEach((b) => {
      b.classList.toggle('is-active', b.dataset.cut === cutType);
    });
    panel.dataset.cutType = cutType;

    const input = panel.querySelector('[data-qty-input]');
    if (cutType === 'By the metre') {
      input.value = '4';
    } else {
      input.value = '1';
    }
    this.updateQtyLabel(panel);
  }

  bumpQty(panel, delta) {
    const input = panel.querySelector('[data-qty-input]');
    const current = parseInt(input.value, 10) || 1;
    input.value = Math.max(1, current + delta);
    this.updateQtyLabel(panel);
  }

  updateQtyLabel(panel) {
    const input = panel.querySelector('[data-qty-input]');
    const label = panel.querySelector('[data-qty-label]');
    if (!input || !label) return;
    const qty = Math.max(1, parseInt(input.value, 10) || 1);
    input.value = qty;
    const cutType = panel.dataset.cutType || 'By the metre';
    if (cutType === 'By the metre') {
      const metres = qty * 0.25;
      const display = metres % 1 === 0 ? metres.toFixed(0) : metres.toString();
      label.textContent = `= ${display} ${metres === 1 ? 'metre' : 'metres'}`;
    } else {
      label.textContent = qty === 1 ? '1 fat quarter' : `${qty} fat quarters`;
    }
  }

  getVariantLabel(v) {
    if (Array.isArray(v.merged_options) && v.merged_options.length) {
      const opt = v.merged_options[0];
      if (typeof opt === 'object' && opt.value) return opt.value;
      if (typeof opt === 'string') return opt.split(':').slice(1).join(':') || opt;
    }
    return v.title || String(v.id);
  }

  getVariantImage(v, product) {
    if (v.image) {
      if (typeof v.image === 'string') return v.image;
      if (v.image.src) return v.image.src;
    }
    return this.getImage(product);
  }

  setVariant(panel, btn) {
    panel.dataset.variantId = btn.dataset.variantOption;
    panel.querySelectorAll('[data-variant-option]').forEach((b) =>
      b.classList.toggle('is-active', b === btn)
    );
    const addBtn = panel.querySelector('[data-config-add]');
    if (addBtn) addBtn.disabled = false;

    const product = this.productsById?.get(panel.dataset.productId);
    const variant = product && Array.isArray(product.variants)
      ? product.variants.find((v) => String(v.id) === String(btn.dataset.variantOption))
      : null;
    const img = panel.querySelector('[data-config-image]');
    if (img && variant && product) {
      img.src = this.sizedImage(this.getVariantImage(variant, product), 200, 200);
    }
  }

  renderConfig(product, availableVariants, selectedVariant) {
    const imgSrc = this.sizedImage(
      selectedVariant ? this.getVariantImage(selectedVariant, product) : this.getImage(product),
      200,
      200
    );
    const title = this.escapeHtml(product.title || '');
    const price = this.formatPrice(product);
    const fabric = this.isFabric(product);
    const multiVariant = availableVariants.length > 1;
    const needsPicker = multiVariant && !fabric;

    const variantPicker = needsPicker
      ? `<div class="cart-drawer-recs__config-group">
          <label class="cart-drawer-recs__config-label">Option</label>
          <div class="cart-drawer-recs__variant-options">
            ${availableVariants
              .map((v) => {
                const label = this.escapeHtml(this.getVariantLabel(v));
                const isSelected = selectedVariant && String(v.id) === String(selectedVariant.id);
                return `<button type="button" class="cart-drawer-recs__variant-option${isSelected ? ' is-active' : ''}" data-variant-option="${v.id}">${label}</button>`;
              })
              .join('')}
          </div>
        </div>`
      : '';

    const fabricOptions = fabric
      ? `<div class="cart-drawer-recs__config-group">
          <label class="cart-drawer-recs__config-label">Cut type</label>
          <div class="cart-drawer-recs__cut-options">
            <button type="button" class="cart-drawer-recs__cut-option is-active" data-cut="By the metre">By length</button>
            <button type="button" class="cart-drawer-recs__cut-option" data-cut="Fat Quarter">Fat Quarter</button>
          </div>
        </div>
        <div class="cart-drawer-recs__config-group">
          <label class="cart-drawer-recs__config-label">Quantity</label>
          <div class="cart-drawer-recs__qty-row">
            <div class="cart-drawer-recs__qty-spinner">
              <button type="button" data-qty-minus aria-label="Decrease">−</button>
              <input type="number" min="1" value="4" data-qty-input>
              <button type="button" data-qty-plus aria-label="Increase">+</button>
            </div>
            <span class="cart-drawer-recs__qty-label" data-qty-label></span>
          </div>
        </div>`
      : '';

    const priceUnit = fabric
      ? `<span class="cart-drawer-recs__config-price-unit">per 0.25m</span>`
      : '';

    return `
      <button type="button" class="cart-drawer-recs__config-back" data-config-back>
        <span aria-hidden="true">←</span> Back
      </button>
      <div class="cart-drawer-recs__config-header">
        <img src="${this.escapeHtml(imgSrc)}" alt="${title}" width="56" height="56" loading="lazy" data-config-image>
        <div class="cart-drawer-recs__config-heading">
          <div class="cart-drawer-recs__config-title">${title}</div>
          <div class="cart-drawer-recs__config-price">${price} ${priceUnit}</div>
        </div>
      </div>
      ${variantPicker}
      ${fabricOptions}
      <button type="button" class="cart-drawer-recs__config-add" data-config-add${needsPicker && !selectedVariant ? ' disabled' : ''}>Add to cart</button>
      <p class="cart-drawer-recs__config-error" data-config-error hidden></p>
    `;
  }

  async handleConfigAdd(panel) {
    const variantId = panel.dataset.variantId;
    if (!variantId) return;
    const btn = panel.querySelector('[data-config-add]');
    const err = panel.querySelector('[data-config-error]');

    const qtyInput = panel.querySelector('[data-qty-input]');
    const isFabric = !!qtyInput;
    const qty = isFabric ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
    const properties = isFabric ? { Cut_type: panel.dataset.cutType || 'By the metre' } : undefined;

    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Adding…';
    err.hidden = true;

    try {
      await this.postCartAdd({ variantId, quantity: qty, properties });
    } catch (e) {
      console.error('[CartDrawerRecs config add]', e);
      err.textContent = e.message || 'Could not add to cart.';
      err.hidden = false;
      btn.disabled = false;
      btn.textContent = original;
    }
  }
}

customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
